import React, { useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { Hash, TimerOutline } from '@lyearn/icons';
import { Stack } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import * as Hero from '@/modules/corporate/components/DetailPageComponents/DetailHero';
import SelectLanguage from '@/modules/corporate/components/DetailPageComponents/DetailHero/components/Languages';
import { FaceVerificationMessageInfo } from '@/modules/corporate/components/FaceVerification/faceVerificationMessageInfo';
import useQuizDetailsHero from '@/modules/corporate/modules/quiz/views/QuizDetails/hooks/useQuizDetailsHero';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { LanguageConfig, LanguageEnum, QuizContentType, ResourceStatusEnum } from '@/types/schema';

import Questions from '../Questions';
import QuizDetailsAction from '../QuizDetailsAction';
import { QuizDetailsHeroProps } from './types';
import { getQuizTimerDuration } from './utils';
const DEFAULT_LANGUAGE = LanguageEnum.English;
function QuizDetailsHero(props: QuizDetailsHeroProps) {
  const { isEmbed, content, parentEntityId, responsive, quizPerformance, learnerId } = props;
  const { isMobile } = useScreen();
  const isAdminRoute = useAdminRoute();
  const {
    languageConfigs,
    languageType,
    id,
    type,
    sourceContent,
    sourceLanguageContentId,
    localizedContents,
    isLocalizedContent,
    defaultLanguageType,
  } = content;

  const heroContent = useQuizDetailsHero(content, quizPerformance);
  const { [FeatureFlags.EnableMultiLanguageContent]: isMultiLanguageContentEnabled } =
    useFeatureFlags();
  const localizedContentsMap = useMemo(() => {
    const map = new Map<string, ResourceStatusEnum>();
    let localizedContents_ = localizedContents;
    if (isLocalizedContent) {
      localizedContents_ = (sourceContent as any)?.localizedContents;
    }
    localizedContents_?.forEach((localizedContent) =>
      // @ts-ignore
      map.set(localizedContent.id, localizedContent.status),
    );
    return map;
  }, [localizedContents]);

  const allLanguageConfigs = useMemo(() => {
    let configs: LanguageConfig[] = (sourceContent as any)?.languageConfigs ?? [];
    if (!configs.length && languageConfigs?.length) {
      configs = languageConfigs;
    }
    if (!isAdminRoute) {
      configs = configs.filter(
        (config) =>
          localizedContentsMap.get(config.contentId) &&
          localizedContentsMap.get(config.contentId) !== ResourceStatusEnum.Draft,
      );
    }
    let defaultLanguage = defaultLanguageType;
    if (isLocalizedContent) {
      defaultLanguage = (sourceContent as QuizContentType).defaultLanguageType ?? undefined;
    }

    return [
      ...configs,
      {
        contentId: sourceLanguageContentId ?? id,
        languageType: defaultLanguage ?? DEFAULT_LANGUAGE,
      },
    ];
  }, [
    languageConfigs,
    sourceContent,
    sourceLanguageContentId,
    id,
    defaultLanguageType,
    isLocalizedContent,
  ]);
  const { t } = useTranslation();

  return (
    <Hero.Main content={heroContent}>
      <Hero.Banner className="flex-shrink-0" />
      <Stack className="px-4 xs:mt-40" direction="col">
        <Hero.Label
          className="mb-4 xs:mb-8"
          color={isMobile ? 'text-text-placeholder' : undefined}
          variant="label-caps"
        />
        <Hero.Title />
        <Stack className="mt-20 flex-wrap xs:mt-16 gap-8">
          <Hero.DueDate />
          <Hero.Duration className="xs:border-interactive-secondary" />
          {allLanguageConfigs.length <= 1 || isEmbed || !isMultiLanguageContentEnabled ? null : (
            <SelectLanguage
              id={id as string}
              languageConfigs={allLanguageConfigs}
              languageType={(languageType ?? defaultLanguageType) as LanguageEnum}
              localizedContentsMap={localizedContentsMap}
              resourceType={type}
            />
          )}
          <Questions
            Icon={Hash}
            text={t('QuizPlayer:Questions', {
              count: content.quizQuestionsConfig?.questionCount || 0,
            })}
          />
          {content.settings?.timeLimit ? (
            <Questions Icon={TimerOutline} text={getQuizTimerDuration(t, content)} />
          ) : null}
        </Stack>
        {!isEmbed && <FaceVerificationMessageInfo />}
        <Hero.Introduction className="mt-24 xs:mt-32" numberOfLines={3} />
        <QuizDetailsAction
          content={content}
          isEmbed={isEmbed}
          learnerId={learnerId}
          parentEntityId={parentEntityId}
          quizPerformance={quizPerformance}
          responsive={responsive}
        />
      </Stack>
    </Hero.Main>
  );
}

export default QuizDetailsHero;
